import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        const hamburger = document.getElementById('mobile-hamburger-btn');
        if (hamburger && !hamburger.contains(e.target as Node)) {
          setIsMobileMenuOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  const isTransparentNav = isHomePage && !isScrolled && !isMobileMenuOpen && !isDropdownOpen;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparentNav
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-lg shadow-sm'
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 font-heading font-bold text-sm">
              P
            </div>
            {isHomePage ? (
              <h1 className={`font-heading text-base md:text-lg font-semibold whitespace-nowrap transition-colors ${
                isTransparentNav ? 'text-white' : 'text-foreground-900'
              }`}>
                Pandabuy Sheet
              </h1>
            ) : (
              <span className={`font-heading text-base md:text-lg font-semibold whitespace-nowrap transition-colors ${
                isTransparentNav ? 'text-white' : 'text-foreground-900'
              }`}>
                Pandabuy Sheet
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-primary-500 ${
                isTransparentNav ? 'text-white/90' : 'text-foreground-700'
              }`}
            >
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors hover:text-primary-500 cursor-pointer ${
                  isTransparentNav ? 'text-white/90' : 'text-foreground-700'
                }`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-56 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 animate-scale-in origin-top-left max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-800 transition-colors whitespace-nowrap"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/pandabuy-spreadsheet-guide"
              className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-primary-500 ${
                isTransparentNav ? 'text-white/90' : 'text-foreground-700'
              }`}
            >
              Shopping Guide
            </Link>

            <Link
              to="/pandabuy-spreadsheet-for-beginners"
              className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-primary-500 ${
                isTransparentNav ? 'text-white/80' : 'text-foreground-600'
              }`}
            >
              Beginners
            </Link>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2 md:gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-3 md:px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs md:text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-xs md:text-sm"></i>
            </a>

            {/* Mobile Hamburger */}
            <button
              id="mobile-hamburger-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                isTransparentNav
                  ? 'text-white hover:bg-white/15'
                  : 'text-foreground-700 hover:bg-background-200'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-lg ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      <div
        ref={mobileMenuRef}
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-background-50 border-t border-background-200/70 px-4 py-4 space-y-1 max-h-[60vh] overflow-y-auto">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-home-line text-base w-5 h-5 flex items-center justify-center"></i>
            Home
          </Link>
          <Link
            to="/pandabuy-spreadsheet-guide"
            className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-book-open-line text-base w-5 h-5 flex items-center justify-center"></i>
            Shopping Guide
          </Link>
          <Link
            to="/pandabuy-spreadsheet-for-beginners"
            className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-user-smile-line text-base w-5 h-5 flex items-center justify-center"></i>
            Beginners Guide
          </Link>

          <div className="pt-3 pb-1">
            <span className="px-3 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Categories</span>
          </div>
          {categories.map((cat) => (
            <a
              key={cat.slug}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer"
            >
              <div className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0"></div>
              {cat.name}
            </a>
          ))}

          <div className="pt-3">
            <Link
              to="/pandabuy-spreadsheet-sneakers"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-article-line text-base w-5 h-5 flex items-center justify-center"></i>
              Sneakers Guide
            </Link>
            <Link
              to="/budget-pandabuy-finds-under-50"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-money-dollar-circle-line text-base w-5 h-5 flex items-center justify-center"></i>
              Budget Finds
            </Link>
            <Link
              to="/pandabuy-spreadsheet-luxury-brands"
              className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-vip-diamond-line text-base w-5 h-5 flex items-center justify-center"></i>
              Luxury Brands
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}